import { difference } from 'lodash'
import {
  CreateHourSeriesCollectionInput,
  HourSeriesCollectionFragment,
  HourSeriesCollectionFragmentDoc,
  HourSeriesFragmentDoc,
  ProjectFragment,
  UpdateHourSeriesCollectionInput,
  useCreateGlobalHourSeriesCollectionsMutation,
  useCreateHourSeriesCollectionMutation,
  useCreateHourSeriesCollectionsMutation,
  useDeleteHourSeriesCollectionsMutation,
  useUpdateHourSeriesCollectionsMutation,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'
import { projectVar } from '../apollo/reactive-vars'

type UseHourSeriesCollections = {
  isLoading: boolean

  createGlobalHourSeriesCollections(
    hourSeriesFile: File,
    shouldOverwrite: boolean,
  ): Promise<HourSeriesCollectionFragment[]>

  createHourSeriesCollections(
    hourSeriesFile: File,
    shouldOverwrite: boolean,
    project: ProjectFragment,
  ): Promise<HourSeriesCollectionFragment[]>

  createHourSeriesCollection(
    project: ProjectFragment,
    input: CreateHourSeriesCollectionInput,
    shouldOverwrite: boolean,
  ): Promise<HourSeriesCollectionFragment>

  updateHourSeriesCollection(
    id: string,
    input: UpdateHourSeriesCollectionInput,
  ): Promise<HourSeriesCollectionFragment>

  deleteHourSeriesCollection(
    hourSeriesCollection: HourSeriesCollectionFragment,
    project?: ProjectFragment,
  ): Promise<boolean>
}

export function useHourSeriesCollections(): UseHourSeriesCollections {
  const [
    createHourSeriesCollections,
    { loading: createHourSeriesCollectionsLoading },
  ] = useCreateHourSeriesCollectionsMutation()
  const [
    createHourSeriesCollection,
    { loading: createHourSeriesCollectionLoading },
  ] = useCreateHourSeriesCollectionMutation()
  const [updateHourSeriesCollection] = useUpdateHourSeriesCollectionsMutation()
  const [deleteHourSeriesCollection] = useDeleteHourSeriesCollectionsMutation()
  const [
    createGlobalHourSeriesCollections,
    { loading: createGlobalHourSeriesCollectionsLoading },
  ] = useCreateGlobalHourSeriesCollectionsMutation()
  const {
    getFragment,
    addItemToList,
    addItemToListInRootQuery,
    removeItemFromList,
    removeItemFromListInRootQuery,
    setField,
  } = useCacheHelpers()

  return {
    isLoading:
      createHourSeriesCollectionsLoading ||
      createGlobalHourSeriesCollectionsLoading ||
      createHourSeriesCollectionLoading,

    async createGlobalHourSeriesCollections(
      hourSeriesFile: File,
      shouldOverwrite: boolean,
    ) {
      const result = await createGlobalHourSeriesCollections({
        variables: {
          hourSeriesFile,
          shouldOverwrite,
        },

        update: (_, mutationResult) => {
          const collections =
            mutationResult.data?.createGlobalHourSeriesCollections
          if (!collections) {
            return
          }

          collections.forEach((collection) =>
            addItemToListInRootQuery(
              HourSeriesCollectionFragmentDoc,
              collection,
              'globalHourSeriesCollections',
            ),
          )
        },
      })

      if (!result.data) {
        throw new Error('Hour series collection creation failed')
      }

      return result.data.createGlobalHourSeriesCollections
    },

    async createHourSeriesCollections(
      hourSeriesFile: File,
      shouldOverwrite: boolean,
      project: ProjectFragment,
    ) {
      const result = await createHourSeriesCollections({
        variables: {
          projectId: project.id,
          hourSeriesFile,
          shouldOverwrite,
        },

        update: (_, mutationResult) => {
          const collections = mutationResult.data?.createHourSeriesCollections
          if (!collections) {
            return
          }

          collections.forEach((collection) =>
            addItemToList(
              HourSeriesCollectionFragmentDoc,
              collection,
              project,
              'hourSeriesCollections',
            ),
          )
        },
      })

      if (!result.data) {
        throw new Error('Hour series collection creation failed')
      }

      return result.data.createHourSeriesCollections
    },

    async createHourSeriesCollection(
      project: ProjectFragment,
      input: CreateHourSeriesCollectionInput,
      shouldOverwrite: boolean,
    ) {
      const result = await createHourSeriesCollection({
        variables: {
          projectId: project.id,
          input,
          shouldOverwrite,
        },

        update: (_, mutationResult) => {
          const collection = mutationResult.data?.createHourSeriesCollection
          if (!collection) {
            return
          }

          addItemToList(
            HourSeriesCollectionFragmentDoc,
            collection,
            project,
            'hourSeriesCollections',
          )
        },
      })

      if (!result.data) {
        throw new Error('Hour series collection creation failed')
      }

      return result.data.createHourSeriesCollection
    },

    async updateHourSeriesCollection(
      id: string,
      input: UpdateHourSeriesCollectionInput,
    ) {
      const collection = getFragment<HourSeriesCollectionFragment>(
        id,
        HourSeriesCollectionFragmentDoc,
      )
      const originalCollectionHourSeriesIds = collection.hourSeries.map(
        (hs) => hs.id,
      )

      const result = await updateHourSeriesCollection({
        variables: { id, input },

        optimisticResponse: {
          updateHourSeriesCollection: {
            ...collection,
            hourSeries:
              collection.hourSeries.filter((hourSeries) =>
                input.hourSeriesIds.includes(hourSeries.id),
              ) ?? [],
          },
        },
      })

      if (!result.data) {
        throw new Error('Hour series collection update failed')
      }

      const updatedCollectionHourSeriesIds =
        result.data.updateHourSeriesCollection.hourSeries.map((hs) => hs.id)

      const removedHourSeriesIds = difference(
        originalCollectionHourSeriesIds,
        updatedCollectionHourSeriesIds,
      )

      const project = projectVar()
      const selectedTemperatureHourSeriesId = project?.temperatureHourSeries?.id

      if (
        project &&
        selectedTemperatureHourSeriesId &&
        removedHourSeriesIds.includes(selectedTemperatureHourSeriesId)
      ) {
        setField(HourSeriesFragmentDoc, null, project, 'temperatureHourSeries')
      }

      return result.data.updateHourSeriesCollection
    },

    async deleteHourSeriesCollection(
      hourSeriesCollection: HourSeriesCollectionFragment,
      project?: ProjectFragment,
    ) {
      const result = await deleteHourSeriesCollection({
        variables: { id: hourSeriesCollection.id },

        update: () => {
          if (project) {
            removeItemFromList(
              HourSeriesCollectionFragmentDoc,
              hourSeriesCollection.id,
              project,
              'hourSeriesCollections',
            )

            if (
              hourSeriesCollection.hourSeries.some(
                (hourSeries) =>
                  hourSeries.id === project.temperatureHourSeries?.id,
              )
            ) {
              setField(
                HourSeriesFragmentDoc,
                null,
                project,
                'temperatureHourSeries',
              )
            }

            return
          }
          removeItemFromListInRootQuery(
            HourSeriesCollectionFragmentDoc,
            hourSeriesCollection.id,
            'globalHourSeriesCollections',
          )
        },
      })

      if (!result.data) {
        throw new Error('Hour series collection deletion failed')
      }

      return result.data.deleteHourSeriesCollection
    },
  }
}
